import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { environmentURLs } from '../../../components/common/CaptureEnvironment';
import { client } from '../../../utils/apolloClient';
import map from 'lodash/map';

const initialState = {
  solutionSubscriptionInfo: {},
  activeSubscriptions: {},
  pendingSubscriptions: {},
  unsubscribedSolutions: {},
  activeBulkSubscriptions: {},
  inActiveBulkSubscriptions: {},
  subscriptionCount: {
    active: 0,
    pending: 0,
    unsubscribed: 0
  },
  showManageScreen: false,
  fetchingSubscriptions: true,
  authorisationStatus: {},
  appProvider: '',
  errorInGettingOrgName: ''
};

export function mapFields(obj) {
  const fieldMapper = {
    subscriptionNotifications: 'subscription_notifications',
    provisioningInterface: 'provisioning_interface',
    unAuthorizedSignon: 'un_authorized_signon',
    registrationDetails: 'registration_details',
    dataRights: 'data_rights',
    customConsent: 'custom_consent',
    selectedCmfNumber: 'entitycmfNumber',
    orgIdList: 'entityIdList',
    orgId: 'entityId',
  };

  if (Array.isArray(obj)) {
    return obj.map(mapFields);
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (key === 'selectedCmfNumber' && Array.isArray(value)) {
          value = value[0]?.toString() || '';
        }

        return [fieldMapper[key] || key, mapFields(value)];
      })
    );
  }

  return obj;
}

export const getSolutionSubscriptionInfo = createAsyncThunk(
  'getSolutionSubscriptionInfo',
  async ({ entityIds, token }) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/v1/subscriptions/entities`,
      data: { entities: entityIds },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return { data: mapFields(response.data) };
  }
);

export const getEntityById = createAsyncThunk(
  'getEntityById',
  async ({ orgId, token }) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      url: `${environmentURLs.ORGANIZATION_URL}/organizations/${orgId}`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return { data: response.data };
  }
);

export const subscriptionManagementSlice = createSlice({
  name: 'subscriptionManagementSlice',
  initialState,
  reducers: {
    getAuthorisationStatus: (state, action) => {
      state.authorisationStatus = action.payload;
    }
  },
  extraReducers: {
    [getSolutionSubscriptionInfo.fulfilled]: (state, action) => {
      state.solutionSubscriptionInfo = action.payload.data.solutionInfo;
      // const subscribed = {'6740b3d1-9a73-4f02-96ad-e814d57340d3': action.payload.data.subscribed[Object.keys(action.payload.data.subscribed)[0]]};
      const subscribed = action.payload.data.subscribed;
      const active = action.payload.data.active;
      const pending = action.payload.data.pending;
      const unsubscribed = action.payload.data.unsubscribed;
      const inactive = action.payload.data.inactive;
      const {canPurchaseOn, authorised} = state.authorisationStatus;
      let solutionSubscriptionInfo_UnAuth = Object.entries(state.solutionSubscriptionInfo).filter(item => item[1].hasOwnProperty('un_authorized_signon') && item[1].un_authorized_signon?.isEnabled);
      solutionSubscriptionInfo_UnAuth = Object.fromEntries(solutionSubscriptionInfo_UnAuth);

      map(subscribed, (subscriptions, key) => {
        let subscription = [];
        if(solutionSubscriptionInfo_UnAuth.hasOwnProperty(key) && authorised){
          subscription = subscriptions;
        }else{
          subscription = subscriptions.filter(item => canPurchaseOn.includes(item.entityId));
        }
        if(subscription.length){
          state.activeSubscriptions[key] = subscription
        }
      });
      map(active, (subscriptions, key) => {
        let subscription = [];
        if(solutionSubscriptionInfo_UnAuth.hasOwnProperty(key) && authorised){
          subscription = subscriptions;
        }else{
          subscription = subscriptions.filter(item => canPurchaseOn.includes(item.entityId));
        }
        if(subscription.length){
          state.activeBulkSubscriptions[key] = subscription
        }
      });
      map(pending, (subscriptions, key) => {
        let subscription = [];
        if(solutionSubscriptionInfo_UnAuth.hasOwnProperty([key]) && authorised){
          subscription = subscriptions;
        }else{
          subscription = subscriptions.filter(item => canPurchaseOn.includes(item.entityId));
        }
        if(subscription.length){
          state.pendingSubscriptions[key] = subscription
        }
      });
      map(unsubscribed, (subscriptions, key) => {
        let subscription = [];
        if(solutionSubscriptionInfo_UnAuth.hasOwnProperty([key]) && authorised){
          subscription = subscriptions;
        }else{
          subscription = subscriptions.filter(item => canPurchaseOn.includes(item.entityId));
        }
        if(subscription.length){
          state.unsubscribedSolutions[key] = subscription
        }
      });
      map(inactive, (subscriptions, key) => {
        let subscription = [];
        if(solutionSubscriptionInfo_UnAuth.hasOwnProperty(key) && authorised){
          subscription = subscriptions;
        }else{
          subscription = subscriptions.filter(item => canPurchaseOn.includes(item.entityId));
        }
        if(subscription.length){
          state.inActiveBulkSubscriptions[key] = subscription
        }
      });

      const solutionInfo = state.solutionSubscriptionInfo;
      let activeCount = 0;
      let pendingCount = 0;
      let unsubscribedCount = 0;
      state.activeSubscriptions && Object.keys(state.activeSubscriptions).forEach(item => {
        if(solutionInfo.hasOwnProperty([item])){
          activeCount++;
        }
      });

      state.activeBulkSubscriptions && Object.keys(state.activeBulkSubscriptions).forEach(item => {
        if(solutionInfo.hasOwnProperty([item])){
          activeCount++;
        }
      });

      state.pendingSubscriptions && Object.keys(state.pendingSubscriptions).forEach(item => {
        if(solutionInfo.hasOwnProperty([item])){
          pendingCount++;
        }
      });

      state.unsubscribedSolutions && Object.keys(state.unsubscribedSolutions).forEach(item => {
        if(solutionInfo.hasOwnProperty([item])){
          unsubscribedCount++;
        }
      });

      state.inActiveBulkSubscriptions && Object.keys(state.inActiveBulkSubscriptions).forEach(item => {
        if(solutionInfo.hasOwnProperty([item])){
          unsubscribedCount++;
        }
      });

      state.subscriptionCount.active = activeCount;
      state.subscriptionCount.pending = pendingCount;
      state.subscriptionCount.unsubscribed = unsubscribedCount;
      state.fetchingSubscriptions = false;
    },
    [getSolutionSubscriptionInfo.pending]: state => {
      state.fetchingSubscriptions = true;
      state.solutionSubscriptionInfo = {};
      state.activeSubscriptions = {};
      state.pendingSubscriptions = {};
      state.unsubscribedSolutions = {};
      state.subscriptionCount = {
        active: 0,
        pending: 0,
        unsubscribed: 0
      };
    },
    [getSolutionSubscriptionInfo.rejected]: state => {
      state.fetchingSubscriptions = false;
    },
    [getEntityById.fulfilled]: (state, action = {payload: {data: {}}}) => {
      state.appProvider = action.payload.data.name;
      state.errorInGettingOrgName = false;
    },
    [getEntityById.pending]: state => {
      state.appProvider = '';
      state.errorInGettingOrgName = false;
    },
    [getEntityById.rejected]: state => {
      state.appProvider = '';
      state.errorInGettingOrgName = true;
    }
  }
});

export const {
  getAuthorisationStatus
} = subscriptionManagementSlice.actions;

export default subscriptionManagementSlice.reducer;
